import React, { useEffect, useMemo, useRef, useState } from "react";
import { LeaveEffectNote } from "./LeaveEffectNote";
import { Save, ChevronDown, ChevronRight, Columns3, Eye, EyeOff, CalendarPlus, FileSpreadsheet, Paperclip } from "lucide-react";
import { formatNum, formatTL } from "@/lib/api";
import LeaveQuickAddModal from "./LeaveQuickAddModal";
import { PayrollExportModal } from "./PayrollExportModal";
import { BankFileButtons } from "./BankFileButtons";
import { EldenFilesModal } from "./EldenFilesModal";
import { IadeCell, EntryBadges, EkNote, liveYemek, liveYol } from "./IadeCell";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";

const DIFF_LIMIT = 0.1;

const STORAGE_KEY = "payroll.hiddenCols.v2";
const DEFAULT_HIDDEN = [
  "fazla_mesai_tutar",
  "yemek_gunluk_tutar",
  "yemek_gun_sayisi",
  "yemek_ekstra",
  "kidem_primi",
  "ek_kesinti",
];

// Columns that can be toggled hidden/visible.
// `editable` means the user can edit this value in the row-expand panel.
const HIDEABLE_COLS = [
  { key: "fazla_mesai_tutar", label: "Fazla Mesai (₺)", editable: true, step: 0.01, testid: (s) => `mesai-${s}` },
  { key: "yemek_gunluk_tutar", label: "Yemek Günlük (₺)", editable: true, step: 0.01, testid: (s) => `yemek-gunluk-${s}` },
  { key: "yemek_gun_sayisi", label: "Yemek Gün", editable: true, step: 0.5, testid: (s) => `yemek-gun-${s}` },
  { key: "yemek_ekstra", label: "Yemek Ekstra (₺)", editable: true, step: 0.01, testid: (s) => `yemek-ekstra-${s}` },
  { key: "kidem_primi", label: "Kıdem Primi (bilgi)", editable: false },
  { key: "ek_kesinti", label: "Ek Kesinti (₺)", editable: true, step: 0.01 },
];

export default function PayrollTable({
  rows,
  expanded,
  onToggle,
  updateLocal,
  saveRow,
  savingId,
  canSeeMoney,
  canSeeKidem,
  canSeeTotals,
  yil,
  ay,
  onReloadRows,
}) {
  const { isAdmin } = useAuth();
  const [quickLeave, setQuickLeave] = useState(null); // { personnel, tur }
  const [bankOnly, setBankOnly] = useState(false);
  const bankMissing = (r) => r.banka_disi && !r.elden;
  const bankMissingCount = rows.filter(bankMissing).length;
  const colSort = useColumnSort(rows, [], {}, true);
  const [exportOpen, setExportOpen] = useState(false);
  const [prevBanka, setPrevBanka] = useState(null);
  useEffect(() => {
    if (!canSeeMoney || !yil || !ay) return;
    api.get(`/payroll/banka-diff?yil=${yil}&ay=${ay}`).then((r) => setPrevBanka(r.data)).catch(() => setPrevBanka(null));
  }, [yil, ay, canSeeMoney]);
  const [eldenBusy, setEldenBusy] = useState("");
  const [eldenFiles, setEldenFiles] = useState([]);
  const [filesFor, setFilesFor] = useState(null);
  const loadEldenFiles = () => api.get(`/payroll/elden-files?yil=${yil}&ay=${ay}`).then((r) => setEldenFiles(r.data.rows)).catch(() => {});
  useEffect(() => { if (canSeeMoney && yil && ay) loadEldenFiles(); }, [yil, ay, canSeeMoney]); // eslint-disable-line react-hooks/exhaustive-deps
  const filesOf = (pid) => eldenFiles.filter((f) => f.personnel_id === pid);
  const toggleElden = async (row, idx) => {
    setEldenBusy(row.personnel_id);
    try {
      await api.put("/payroll/elden", { personnel_id: row.personnel_id, yil, ay, elden: !row.elden });
      updateLocal(idx, "elden", !row.elden);
    } catch { toast.error("Elden işareti kaydedilemedi"); } finally { setEldenBusy(""); }
  };
  const diffOf = (row) => {
    const prev = prevBanka?.rows?.[row.personnel_id];
    if (!prev || prev <= 0) return null;
    const pct = (row.banka_listesi - prev) / prev;
    return Math.abs(pct) >= DIFF_LIMIT ? { pct, prev } : null;
  };
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return new Set(DEFAULT_HIDDEN);
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...hidden])); } catch {}
  }, [hidden]);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isHidden = (k) => hidden.has(k);
  const toggleCol = (k) => setHidden((h) => {
    const n = new Set(h);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const showAll = () => setHidden(new Set());
  const resetDefault = () => setHidden(new Set(DEFAULT_HIDDEN));

  // Compute total column count for the expand-row colspan.
  // Base 23 columns (chevron, personel, sabit, calisma, u_izin, uc_izin, raporlu,
  //   fazla_mesai, yemek_gunluk, yemek_gun, yemek_ekstra, yemek_toplam, yol_gunluk,
  //   yol_ucreti, prim_oto, manuel_prim, kidem_primi, avans, ek_kesinti, toplam_kesinti,
  //   net, brut, save). If !canSeeKidem the Kıdem column is not rendered at all.
  const baseCols = canSeeKidem ? 25 : 24;
  const hiddenEffective = useMemo(() => {
    const arr = [...hidden];
    return arr.filter((k) => (k === "kidem_primi" ? canSeeKidem : true)).length;
  }, [hidden, canSeeKidem]);
  const visibleCols = baseCols - hiddenEffective;

  // How many hideable "money/mesai" columns sit in the middle of the totals row
  // (fazla_mesai, yemek_gunluk, yemek_gun, yemek_ekstra). Used nowhere now — totals
  // row renders each cell individually.
  const hideableKidemCount = canSeeKidem ? 1 : 0;

  return (
    <div className={canSeeMoney ? "hidden md:block" : "hidden"}>
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-2 px-3 pt-3 flex-wrap">
        {prevBanka && (
          <span className="mr-auto text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1" data-testid="banka-diff-legend">
            Turuncu satırlar: Banka Listesi önceki aya ({prevBanka.label}) göre %{DIFF_LIMIT * 100} veya daha fazla değişti
          </span>
        )}
        {(bankMissingCount > 0 || bankOnly) && (
          <button type="button" onClick={() => setBankOnly((v) => !v)} data-testid="payroll-filter-bank-missing"
            title="IBAN'ı olmayan veya banka listesinden çıkarılan (elden işaretlenmemiş) personeli göster"
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${bankOnly ? "bg-sky-600 text-white border-sky-600" : "bg-sky-50 text-sky-800 border-sky-300 hover:bg-sky-100"} ${prevBanka ? "" : "mr-auto"}`}>
            {bankOnly ? "Tümünü göster" : "Banka listesinde olmayanlar"} · {bankMissingCount}
          </button>
        )}
        <BankFileButtons yil={yil} ay={ay} onReload={onReloadRows} />
        <button
          type="button"
          data-testid="payroll-export-open"
          onClick={() => setExportOpen(true)}
          className="inline-flex items-center gap-1.5 bg-green-700 hover:bg-green-800 text-white px-3 py-1.5 rounded-md text-xs font-semibold"
        >
          <FileSpreadsheet size={13} /> Sütun Seçerek Aktar
        </button>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            data-testid="payroll-cols-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md text-xs font-semibold"
          >
            <Columns3 size={13} /> Sütunlar
            {hidden.size > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-900 text-white text-[10px] font-bold">
                {hidden.size}
              </span>
            )}
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-slate-200 rounded-lg shadow-xl p-2"
              data-testid="payroll-cols-menu"
            >
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Gizlenebilir sütunlar
              </div>
              {HIDEABLE_COLS.map((c) => {
                if (c.key === "kidem_primi" && !canSeeKidem) return null;
                const visible = !isHidden(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`payroll-col-${c.key}`}
                    onClick={() => toggleCol(c.key)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 text-xs text-slate-700"
                  >
                    <span className="flex items-center gap-2">
                      {visible ? <Eye size={13} className="text-emerald-600" /> : <EyeOff size={13} className="text-slate-400" />}
                      {c.label}
                    </span>
                    <span className={visible ? "text-[10px] font-semibold text-emerald-700" : "text-[10px] font-semibold text-slate-400"}>
                      {visible ? "AÇIK" : "GİZLİ"}
                    </span>
                  </button>
                );
              })}
              <div className="flex items-center justify-between gap-2 border-t border-slate-200 mt-2 pt-2">
                <button
                  type="button"
                  onClick={showAll}
                  className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                  data-testid="payroll-cols-show-all"
                >Tümünü Göster</button>
                <button
                  type="button"
                  onClick={resetDefault}
                  className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                  data-testid="payroll-cols-reset"
                >Varsayılan</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="sticky-table">
      <SortProvider value={colSort}>
      <table className="payroll-table w-full" data-testid="payroll-table">
        <thead>
          <tr>
            <th style={{ width: 32 }}></th>
            <STh k="ad_soyad">Personel</STh>
            <STh k="sabit_net_maas" className="text-right" title="Personel kaydındaki net maaş · kesinti ve eklemelerden etkilenmez">Sabit Maaş (Net)</STh>
            <STh k="calisma_gunu" className="text-right">Çalışılan Gün</STh>
            <STh k="ucretli_izin_gun" className="text-right">Ücretli İzin</STh>
            <STh k="ucretsiz_izin_gun" className="text-right">Ücretsiz İzin</STh>
            <STh k="raporlu_gun" className="text-right">Raporlu Gün</STh>
            {!isHidden("fazla_mesai_tutar") && <STh k="fazla_mesai_tutar" className="text-right">Fazla Mesai (₺)</STh>}
            {!isHidden("yemek_gunluk_tutar") && <STh k="yemek_gunluk_tutar" className="text-right">Yemek Günlük (₺)</STh>}
            {!isHidden("yemek_gun_sayisi") && <STh k="yemek_gun_sayisi" className="text-right">Yemek Gün</STh>}
            {!isHidden("yemek_ekstra") && <STh k="yemek_ekstra" className="text-right">Yemek Ekstra (₺)</STh>}
            <STh k="yemek_toplam" className="text-right text-indigo-200">Yemek Toplam*</STh>
            <STh k="yol_gunluk_ucret" className="text-right">Yol Günlük (₺)</STh>
            <STh k="yol_ucreti" className="text-right">Yol Ücreti</STh>
            <STh k="prim_from_records" className="text-right">Prim (Oto)</STh>
            <STh k="manuel_prim" className="text-right">Manuel Prim (₺)</STh>
            {canSeeKidem && !isHidden("kidem_primi") && <STh k="kidem_primi" className="text-right text-emerald-200">Kıdem Primi*</STh>}
            <STh k="avans_kesintisi" className="text-right">Avans Kesinti</STh>
            {!isHidden("ek_kesinti") && <STh k="ek_kesinti" className="text-right">Ek Kesinti</STh>}
            <STh k="toplam_kesinti" className="text-right">Toplam Kesinti</STh>
            <STh k="net_maas" className="text-right">Net Ücret</STh>
            <STh k="brut_maas" className="text-right">Brüt Ücret</STh>
            <STh k="banka_listesi" className="text-right text-sky-200" title="Net maaş − primler (yol dahil, yemek hariç)">Banka Listesi</STh>
            <STh k="prim_odemesi" className="text-right text-emerald-200" title="Oto prim + manuel prim + başarı primi · ayrı ödenir">Prim Ödemesi</STh>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {colSort.sorted.filter((r) => !bankOnly || bankMissing(r)).map((row) => {
            // idx must point at the unsorted `rows` array (updateLocal indexes it)
            const idx = rows.indexOf(row);
            const isOpen = !!expanded[row.personnel_id];
            const diff = diffOf(row);
            return (
              <React.Fragment key={row.personnel_id}>
                <tr
                  data-testid={`payroll-row-${row.sicil_no}`}
                  className={diff ? "bg-amber-50 [&>td]:bg-amber-50" : row.banka_disi && !row.elden ? "bg-sky-100 [&>td]:bg-sky-100" : isOpen ? "bg-slate-50" : ""}
                  data-diff={diff ? "1" : undefined}
                  data-banka-disi={row.banka_disi && !row.elden ? "1" : undefined}
                  title={row.banka_disi && !row.elden ? `Banka listesinde yok (${row.banka_disi}) · elden ödenecekse "E" ile işaretleyin` : undefined}
                >
                  <td className="text-center">
                    <button
                      onClick={() => onToggle(row.personnel_id)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-slate-100"
                      data-testid={`payroll-row-toggle-${row.sicil_no}`}
                      aria-label={isOpen ? "Kapat" : "Aç"}
                    >
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  </td>
                  <td
                    onClick={() => onToggle(row.personnel_id)}
                    className="cursor-pointer select-none"
                    data-testid={`payroll-row-name-${row.sicil_no}`}
                  >
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      {row.ad_soyad}
                      <EntryBadges row={row} />
                      {row.yillik_hak_asimi && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] font-black uppercase bg-rose-100 text-rose-800 border border-rose-300 px-1.5 py-0.5 rounded animate-pulse"
                          title={`Yıllık izin aşımı: Hak ${row.yillik_hak_edilen} · Kullanılan ${row.yillik_kullanilan} · Kalan ${row.yillik_kalan}`}
                          data-testid={`hak-asimi-${row.sicil_no}`}
                        >
                          ⚠ HAK AŞIMI {row.yillik_kalan}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{row.pozisyon} · {row.sicil_no}</div>
                    <LeaveEffectNote row={row} canSeeMoney={canSeeMoney} />
                    {row.kesinti_notu && (
                      <div className="text-[10px] font-semibold text-sky-700 mt-0.5 truncate max-w-[260px]" title={`Banka açıklaması: ${row.kesinti_notu}`}
                        data-testid={`kesinti-notu-${row.sicil_no}`}>{row.kesinti_notu}</div>
                    )}
                    {row.puantaj_totals && (
                      <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`puantaj-badge-${row.sicil_no}`}>
                        {row.puantaj_totals.calisma > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" title="Çalışıldı">
                            Ç{row.puantaj_totals.calisma}
                          </span>
                        )}
                        {row.puantaj_totals.ucretli_izin > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800" title="Ücretli İzin">
                            Üİ{row.puantaj_totals.ucretli_izin}
                          </span>
                        )}
                        {row.puantaj_totals.ucretsiz_izin > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Ücretsiz İzin">
                            Üs{row.puantaj_totals.ucretsiz_izin}
                          </span>
                        )}
                        {row.puantaj_totals.raporlu > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800" title="Raporlu">
                            R{row.puantaj_totals.raporlu}
                          </span>
                        )}
                        {row.puantaj_totals.tatil > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800" title="Resmi Tatil">
                            T{row.puantaj_totals.tatil}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="num-cell text-slate-700" data-testid={`sabit-maas-${row.sicil_no}`} title="Personel kaydındaki net maaş">{formatNum(row.sabit_net_maas)}</td>
                  <td>
                    <input data-testid={`calisma-gun-${row.sicil_no}`} type="number" step="0.5" value={row.calisma_gunu}
                      onChange={(e) => updateLocal(idx, "calisma_gunu", e.target.value)} />
                  </td>
                  <td>
                    <div className="flex items-start gap-1">
                      <input type="number" step="0.5" data-testid={`ucretli-izin-${row.sicil_no}`}
                        title={Number(row.ucretli_izin_yillik) > 0 ? `${row.ucretli_izin_yillik} gün yıllık izin ekranından otomatik geldi (değiştirmek için Yıllık İzinler)` : "Ücretli izin günü"}
                        value={Number(row.ucretli_izin_manuel ?? 0) + Number(row.ucretli_izin_yillik || 0)}
                        onChange={(e) => updateLocal(idx, "ucretli_izin_manuel", Math.max(0, (Number(e.target.value) || 0) - Number(row.ucretli_izin_yillik || 0)))} />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setQuickLeave({ personnel: { id: row.personnel_id, ad_soyad: row.ad_soyad }, tur: "ucretli_izin" }); }}
                        className="shrink-0 w-6 h-6 grid place-items-center rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
                        title="Tarih aralığı ile ücretli izin ekle"
                        data-testid={`quick-ucretli-${row.sicil_no}`}
                      >
                        <CalendarPlus size={11} />
                      </button>
                    </div>
                    {Number(row.ucretli_izin_yillik) > 0 && (
                      <div
                        className="mt-0.5 text-[9px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1 py-0.5 inline-block"
                        title={`Yıllık izin ekranından ${row.ucretli_izin_yillik} gün otomatik eklendi. Toplam ücretli izin: ${row.ucretli_izin_gun}`}
                        data-testid={`ucretli-yillik-${row.sicil_no}`}
                      >
                        +{row.ucretli_izin_yillik} yıllık · Σ{row.ucretli_izin_gun}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex items-start gap-1">
                      <input type="number" step="0.5" value={row.ucretsiz_izin_manuel ?? row.ucretsiz_izin_gun}
                        onChange={(e) => updateLocal(idx, "ucretsiz_izin_manuel", e.target.value)} />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setQuickLeave({ personnel: { id: row.personnel_id, ad_soyad: row.ad_soyad }, tur: "ucretsiz_izin" }); }}
                        className="shrink-0 w-6 h-6 grid place-items-center rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700"
                        title="Tarih aralığı ile ücretsiz izin ekle"
                        data-testid={`quick-ucretsiz-${row.sicil_no}`}
                      >
                        <CalendarPlus size={11} />
                      </button>
                    </div>
                    {Number(row.ucretsiz_izin_yillik) > 0 && (
                      <div className="mt-0.5 text-[9px] font-bold text-slate-700 bg-slate-100 border border-slate-200 rounded px-1 py-0.5 inline-block"
                           title={`Yıllık izin ekranından ${row.ucretsiz_izin_yillik} gün. Toplam: ${row.ucretsiz_izin_gun}`}>
                        +{row.ucretsiz_izin_yillik} yıllık · Σ{row.ucretsiz_izin_gun}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex items-start gap-1">
                      <input type="number" step="0.5" value={row.raporlu_manuel ?? row.raporlu_gun}
                        onChange={(e) => updateLocal(idx, "raporlu_manuel", e.target.value)} />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setQuickLeave({ personnel: { id: row.personnel_id, ad_soyad: row.ad_soyad }, tur: "rapor" }); }}
                        className="shrink-0 w-6 h-6 grid place-items-center rounded border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700"
                        title="Tarih aralığı ile rapor ekle"
                        data-testid={`quick-rapor-${row.sicil_no}`}
                      >
                        <CalendarPlus size={11} />
                      </button>
                    </div>
                    {Number(row.raporlu_yillik) > 0 && (
                      <div className="mt-0.5 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1 py-0.5 inline-block"
                           title={`Rapor kaydından ${row.raporlu_yillik} gün. Toplam: ${row.raporlu_gun}`}>
                        +{row.raporlu_yillik} rapor · Σ{row.raporlu_gun}
                      </div>
                    )}
                  </td>
                  {!isHidden("fazla_mesai_tutar") && (
                    <td>
                      <input data-testid={`mesai-${row.sicil_no}`} type="number" step="0.01" value={row.fazla_mesai_tutar}
                        onChange={(e) => updateLocal(idx, "fazla_mesai_tutar", e.target.value)} />
                    </td>
                  )}
                  {!isHidden("yemek_gunluk_tutar") && (
                    <td>
                      <input data-testid={`yemek-gunluk-${row.sicil_no}`} type="number" step="0.01" value={row.yemek_gunluk_tutar}
                        className={row.yemek_gunluk_manuel ? "!bg-amber-50 !border-amber-300" : ""}
                        title={row.yemek_gunluk_manuel ? `Bu ay elle girildi · otomatik: ${row.yemek_gunluk_oto}` : "Otomatik (kişi/pozisyon günlük yemek ücreti)"}
                        onChange={(e) => { updateLocal(idx, "yemek_gunluk_tutar", e.target.value); updateLocal(idx, "yemek_gunluk_manuel", true); }} />
                      {row.yemek_gunluk_manuel && (
                        <button type="button" data-testid={`yemek-gunluk-reset-${row.sicil_no}`} title={`Otomatiğe dön (${row.yemek_gunluk_oto})`}
                          onClick={() => { updateLocal(idx, "yemek_gunluk_tutar", row.yemek_gunluk_oto); updateLocal(idx, "yemek_gunluk_manuel", false); }}
                          className="block text-[9px] font-bold text-amber-700 hover:underline">ELLE · oto'ya dön</button>
                      )}
                    </td>
                  )}
                  {!isHidden("yemek_gun_sayisi") && (
                    <td>
                      <input data-testid={`yemek-gun-${row.sicil_no}`} type="number" step="0.5" value={row.yemek_gun_sayisi}
                        className={row.yemek_gun_manuel ? "!bg-amber-50 !border-amber-300" : ""}
                        title={row.yemek_gun_manuel ? `Bu ay elle girildi · puantaj: ${row.yemek_gun_oto} gün` : "Otomatik · puantajdaki çalışılan günler"}
                        onChange={(e) => { updateLocal(idx, "yemek_gun_sayisi", e.target.value); updateLocal(idx, "yemek_gun_manuel", true); }} />
                      {row.yemek_gun_manuel && row.yemek_gun_auto !== false && (
                        <button type="button" data-testid={`yemek-gun-reset-${row.sicil_no}`} title={`Otomatiğe dön (${row.yemek_gun_oto} gün)`}
                          onClick={() => { updateLocal(idx, "yemek_gun_sayisi", row.yemek_gun_oto); updateLocal(idx, "yemek_gun_manuel", false); }}
                          className="block text-[9px] font-bold text-amber-700 hover:underline">ELLE · oto'ya dön</button>
                      )}
                    </td>
                  )}
                  {!isHidden("yemek_ekstra") && (
                    <td>
                      <input data-testid={`yemek-ekstra-${row.sicil_no}`} type="number" step="0.01" value={row.yemek_ekstra || 0}
                        onChange={(e) => updateLocal(idx, "yemek_ekstra", e.target.value)} />
                    </td>
                  )}
                  <td className="num-cell text-indigo-700 font-semibold" title={`Sonraki ay avansı: ${row.yemek_gun_sayisi} gün · Bu ay çalışılmayan ${row.yemek_iade_gun || 0} gün iade · Kart yüklemesi (Net'e dahil değil)`}>
                    <span data-testid={`yemek-toplam-${row.sicil_no}`}>{formatNum(liveYemek(row).toplam)}</span>
                    <EkNote row={row} kind="yemek" />
                    <IadeCell row={row} idx={idx} kind="yemek" isAdmin={isAdmin} updateLocal={updateLocal} />
                    {liveYemek(row).kesinti > 0 && <div className="text-[10px] font-bold text-rose-700" data-testid={`yemek-maas-kesinti-${row.sicil_no}`}>maaştan −{formatNum(liveYemek(row).kesinti)}</div>}
                  </td>
                  <td>
                    <input data-testid={`yol-gunluk-${row.sicil_no}`} type="number" step="0.01" value={row.yol_gunluk_ucret ?? ""}
                      className={row.yol_gunluk_manuel ? "!bg-amber-50 !border-amber-300" : ""}
                      title={row.yol_gunluk_manuel ? `Bu ay elle girildi · otomatik: ${row.yol_gunluk_oto}` : "Otomatik (yıllık aylık yol ÷ 30)"}
                      onChange={(e) => updateLocal(idx, "yol_gunluk_ucret", e.target.value)} />
                    {row.yol_gunluk_manuel && (
                      <button type="button" data-testid={`yol-gunluk-reset-${row.sicil_no}`} title={`Otomatiğe dön (${row.yol_gunluk_oto})`}
                        onClick={() => { updateLocal(idx, "yol_gunluk_ucret", row.yol_gunluk_oto); updateLocal(idx, "yol_gunluk_manuel", false); }}
                        className="block text-[9px] font-bold text-amber-700 hover:underline">ELLE · oto'ya dön</button>
                    )}
                  </td>
                  <td className="num-cell text-slate-900 font-semibold" title={`Sonraki ay avansı ${formatNum(row.yol_avans)} − bu ay ${30 - (row.yol_calisma_gunu ?? 30)} gün iade ${formatNum(row.yol_iade)}`}>
                    <span data-testid={`yol-ucreti-${row.sicil_no}`} className={liveYol(row) < 0 ? "text-rose-700" : ""}>{formatNum(liveYol(row))}</span>
                    <EkNote row={row} kind="yol" />
                    <IadeCell row={row} idx={idx} kind="yol" isAdmin={isAdmin} updateLocal={updateLocal} />
                  </td>
                  <td className="num-cell text-emerald-700 font-semibold" title={`Giden: ${formatNum(row.prim_giden)} + Araştıran: ${formatNum(row.prim_arastiran)}${row.prim_cc_source_count ? `\n(${row.prim_cc_source_count} satır Call Center %7 kuralından geldi)` : ""}`}>
                    <span className="inline-flex items-center gap-1" data-testid={`prim-from-records-${row.sicil_no}`}>
                      {formatNum(row.prim_from_records || 0)}
                      {row.prim_cc_source_count > 0 && (
                        <span
                          className="inline-flex items-center px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold border border-amber-200"
                          title={`${row.prim_cc_source_count} prim satırı Call Center %7 kuralından hesaplandı`}
                          data-testid={`cc-badge-row-${row.sicil_no}`}
                        >
                          %7 CC
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    <input data-testid={`manuel-prim-${row.sicil_no}`} type="number" step="0.01" value={row.manuel_prim || 0}
                      onChange={(e) => updateLocal(idx, "manuel_prim", e.target.value)} />
                  </td>
                  {canSeeKidem && !isHidden("kidem_primi") && (
                    <td className="num-cell text-emerald-700 font-semibold" title={`${row.kidem_yillari || 0} yıl kıdem · Bilgi amaçlı · Net'e dahil değil`}>
                      {formatNum(row.kidem_primi || 0)}
                    </td>
                  )}
                  <td className="num-cell deduction-cell">{formatNum(row.avans_kesintisi)}</td>
                  {!isHidden("ek_kesinti") && (
                    <td>
                      <input type="number" step="0.01" value={row.ek_kesinti}
                        onChange={(e) => updateLocal(idx, "ek_kesinti", e.target.value)} />
                    </td>
                  )}
                  <td className="num-cell deduction-cell">{formatNum(row.toplam_kesinti)}</td>
                  <td className="num-cell net-cell" data-testid={`net-${row.sicil_no}`}>{formatNum(row.net_maas)}</td>
                  <td className="num-cell text-slate-700 font-semibold" title="Brüt = Normal Maaş + Fazla Mesai">{formatNum(row.brut_maas)}</td>
                  <td className="num-cell text-sky-700 font-bold" data-testid={`banka-${row.sicil_no}`} title={diff ? `${prevBanka.label}: ${formatNum(diff.prev)}` : "Net maaş − primler (yol dahil, yemek hariç)"}>
                    <div className="flex items-center justify-end gap-1.5">
                      <button type="button" onClick={() => toggleElden(row, idx)} disabled={eldenBusy === row.personnel_id}
                        data-testid={`elden-toggle-${row.sicil_no}`}
                        title={row.elden ? "Elden ödenecek · banka talimatına girmez (sonraki aylarda da işaretli gelir)" : "Elden ödenecek olarak işaretle"}
                        className={`px-1.5 py-0.5 rounded text-[9px] font-black border transition-colors disabled:opacity-50 ${row.elden ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-300 text-slate-400 hover:border-violet-400 hover:text-violet-600"}`}>
                        ELDEN
                      </button>
                      {row.elden && (
                        <button type="button" onClick={() => setFilesFor(row)} data-testid={`elden-files-${row.sicil_no}`}
                          title={filesOf(row.personnel_id).length ? `${filesOf(row.personnel_id).length} imzalı makbuz yüklü` : "İmzalı makbuz yükle"}
                          className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold border ${filesOf(row.personnel_id).length ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-700"}`}>
                          <Paperclip size={10} />{filesOf(row.personnel_id).length || "!"}
                        </button>
                      )}
                      <span className={row.elden ? "line-through text-slate-400" : ""}>{formatNum(row.banka_listesi)}</span>
                    </div>
                    {diff && (
                      <div className={`text-[10px] font-bold ${diff.pct > 0 ? "text-amber-700" : "text-rose-700"}`} data-testid={`banka-diff-${row.sicil_no}`}>
                        {diff.pct > 0 ? "▲" : "▼"} %{Math.abs(diff.pct * 100).toFixed(0)} · önceki {formatNum(diff.prev)}
                      </div>
                    )}
                  </td>
                  <td className="num-cell text-emerald-700 font-semibold">
                    <span data-testid={`prim-odeme-${row.sicil_no}`}>{formatNum(row.prim_odemesi || 0)}</span>
                    {row.avans_prim_kesinti > 0 && <div className="text-[10px] font-bold text-rose-600" data-testid={`prim-avans-${row.sicil_no}`}>avans −{formatNum(row.avans_prim_kesinti)}</div>}
                    {row.avans_prim_devir > 0 && <div className="text-[10px] font-semibold text-amber-700" title="Prim yetmediği için sonraki aya devreden avans" data-testid={`prim-avans-devir-${row.sicil_no}`}>devir {formatNum(row.avans_prim_devir)}</div>}
                  </td>
                  <td>
                    <button
                      data-testid={`save-row-${row.sicil_no}`}
                      onClick={() => saveRow(row)}
                      disabled={savingId === row.personnel_id}
                      className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50"
                    >
                      <Save size={12} />
                      {savingId === row.personnel_id ? "..." : "Kaydet"}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50">
                    <td></td>
                    <td colSpan={visibleCols - 1} className="py-4">
                      {/* Hidden column inputs */}
                      <HiddenColsPanel
                        row={row}
                        idx={idx}
                        hidden={hidden}
                        canSeeKidem={canSeeKidem}
                        updateLocal={updateLocal}
                      />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2 text-sm max-w-5xl">
                        <Detail label="Günlük Ücret" value={formatTL(row.gunluk_ucret)} />
                        <Detail label="Normal Maaş" value={formatTL(row.normal_maas)} />
                        <Detail label="Brüt Maaş (Normal + Mesai)" value={formatTL(row.brut_maas)} />
                        <Detail label="Yol Ücreti (+)" value={formatTL(row.yol_ucreti || 0)} tone="emerald" />
                        <Detail label="SGK + İşsizlik (%15)" value={formatTL(row.sgk_kesinti)} tone="rose" />
                        <Detail label="Gelir Vergisi (%15)" value={formatTL(row.gelir_vergisi)} tone="rose" />
                        <Detail label="Damga Vergisi" value={formatTL(row.damga_vergisi)} tone="rose" />
                        <Detail label="Rapor Kesintisi" value={formatTL(row.rapor_kesinti)} tone="rose" />
                        <Detail label="Ücretsiz İzin Kesintisi" value={formatTL(row.ucretsiz_izin_kesinti)} tone="rose" />
                        <Detail label="Avans Kesintisi" value={formatTL(row.avans_kesintisi)} tone="rose" />
                        <Detail label="Ek Kesinti" value={formatTL(row.ek_kesinti)} tone="rose" />
                        <Detail label="Prim (Oto) (+)" value={formatTL(row.prim_from_records || 0)} tone="emerald" />
                        <Detail label="  → Giden Payı" value={formatTL(row.prim_giden || 0)} tone="emerald" />
                        <Detail label="  → Araştırma Primi" value={formatTL(row.prim_arastiran || 0)} tone="indigo" />
                        {row.prim_cc_source_count > 0 && (
                          <Detail
                            label="  → Kaynak"
                            value={`${row.prim_cc_source_count} satır Call Center %7 kuralı`}
                            tone="amber"
                          />
                        )}
                        {row.manuel_prim > 0 && (
                          <Detail label="Manuel Prim (+)" value={formatTL(row.manuel_prim)} tone="emerald" />
                        )}
                        {row.basari_primi > 0 && (
                          <Detail label="Başarı Primi (+)" value={formatTL(row.basari_primi)} tone="emerald" />
                        )}
                        <Detail label="Yemek Toplam (bilgi)" value={formatTL(row.yemek_toplam)} tone="indigo" />
                        {canSeeKidem && row.kidem_primi > 0 && (
                          <Detail label={`Kıdem Primi (${row.kidem_yillari} yıl, bilgi)`} value={formatTL(row.kidem_primi)} tone="indigo" />
                        )}
                        <Detail label="NET ÜCRET" value={formatTL(row.net_maas)} tone="emerald" strong />
                        <Detail label="BANKA LİSTESİ" value={formatTL(row.banka_listesi)} strong />
                        <Detail label="PRİM ÖDEMESİ" value={formatTL(row.prim_odemesi || 0)} tone="emerald" strong />
                        <Detail label="IBAN" value={row.iban || "— girilmemiş —"} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        {canSeeTotals && rows.length > 0 && (
          <tfoot data-testid="payroll-totals-footer">
            <tr className="bg-slate-100 font-bold">
              <td colSpan={2} className="px-3 py-3 text-slate-900">TOPLAM</td>
              <td className="num-cell">{formatNum(rows.reduce((s, r) => s + (r.sabit_net_maas || 0), 0))}</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              {!isHidden("fazla_mesai_tutar") && <td></td>}
              {!isHidden("yemek_gunluk_tutar") && <td></td>}
              {!isHidden("yemek_gun_sayisi") && <td></td>}
              {!isHidden("yemek_ekstra") && <td></td>}
              <td className="num-cell text-indigo-700">{formatNum(rows.reduce((s, r) => s + liveYemek(r).toplam, 0))}</td>
              <td></td>
              <td className="num-cell text-slate-900">{formatNum(rows.reduce((s, r) => s + liveYol(r), 0))}</td>
              <td className="num-cell text-emerald-700">{formatNum(rows.reduce((s, r) => s + (r.prim_from_records || 0), 0))}</td>
              <td></td>
              {canSeeKidem && !isHidden("kidem_primi") && <td className="num-cell text-emerald-700">{formatNum(rows.reduce((s, r) => s + (r.kidem_primi || 0), 0))}</td>}
              <td className="num-cell deduction-cell">{formatNum(rows.reduce((s, r) => s + r.avans_kesintisi, 0))}</td>
              {!isHidden("ek_kesinti") && <td></td>}
              <td className="num-cell deduction-cell">{formatNum(rows.reduce((s, r) => s + r.toplam_kesinti, 0))}</td>
              <td className="num-cell net-cell" data-testid="total-net">{formatNum(rows.reduce((s, r) => s + r.net_maas, 0))}</td>
              <td className="num-cell text-slate-700">{formatNum(rows.reduce((s, r) => s + (r.brut_maas || 0), 0))}</td>
              <td className="num-cell text-sky-700" data-testid="total-banka" title={`Elden: ${formatNum(rows.filter((r) => r.elden).reduce((s, r) => s + (r.banka_listesi || 0), 0))}`}>
                {formatNum(rows.filter((r) => !r.elden).reduce((s, r) => s + (r.banka_listesi || 0), 0))}
                {rows.some((r) => r.elden) && (
                  <div className="text-[10px] text-violet-700" data-testid="total-elden">+ elden {formatNum(rows.filter((r) => r.elden).reduce((s, r) => s + (r.banka_listesi || 0), 0))}</div>
                )}
              </td>
              <td className="num-cell text-emerald-700" data-testid="total-prim-odeme">{formatNum(rows.reduce((s, r) => s + (r.prim_odemesi || 0), 0))}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
      </SortProvider>
      </div>
      {filesFor && (
        <EldenFilesModal row={filesFor} yil={yil} ay={ay} files={filesOf(filesFor.personnel_id)}
          onChanged={loadEldenFiles} onClose={() => setFilesFor(null)} />
      )}
      {exportOpen && <PayrollExportModal yil={yil} ay={ay} onClose={() => setExportOpen(false)} />}
      {quickLeave && (
        <LeaveQuickAddModal
          personnel={quickLeave.personnel}
          tur={quickLeave.tur}
          yil={yil}
          ay={ay}
          onClose={() => setQuickLeave(null)}
          onSaved={() => { setQuickLeave(null); onReloadRows?.(); }}
        />
      )}
    </div>
  );
}

function HiddenColsPanel({ row, idx, hidden, canSeeKidem, updateLocal }) {
  const hiddenEditable = HIDEABLE_COLS.filter((c) => {
    if (!hidden.has(c.key)) return false;
    if (c.key === "kidem_primi" && !canSeeKidem) return false;
    return true;
  });
  if (hiddenEditable.length === 0) return null;
  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-lg p-3" data-testid={`payroll-hidden-panel-${row.sicil_no}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
        Gizli sütunlar · düzenlemek için aşağıdaki alanları kullanın
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {hiddenEditable.map((c) => (
          <div key={c.key}>
            <label className="text-[11px] font-semibold text-slate-600 block mb-1">{c.label}</label>
            {c.editable ? (
              <input
                type="number"
                step={c.step}
                value={row[c.key] ?? 0}
                onChange={(e) => updateLocal(idx, c.key, e.target.value)}
                data-testid={c.testid ? `${c.testid(row.sicil_no)}-hidden` : `hidden-input-${c.key}-${row.sicil_no}`}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm font-mono-tab outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
              />
            ) : (
              <div className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono-tab text-emerald-700 font-semibold">
                {formatNum(row[c.key] || 0)}
                {c.key === "kidem_primi" && row.kidem_yillari > 0 && (
                  <span className="ml-2 text-[10px] text-slate-500">({row.kidem_yillari} yıl)</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Detail({ label, value, tone, strong }) {
  const toneCls = tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : tone === "indigo" ? "text-indigo-700" : tone === "amber" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="flex items-center justify-between gap-4 py-1 border-b border-slate-200">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`font-mono-tab ${strong ? "font-bold text-base" : "font-semibold"} ${toneCls}`}>{value}</span>
    </div>
  );
}
